import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { axiosImageAPI } from '@/axios';
import {
  addFilesToAttachment,
  createAttachment,
  deleteFilesInsideAttachment,
} from './api';

export default {
  computed: {
    ...mapGetters([
      'attachmentID',
      'errors',
      'failedToUpload',
      'uploadedFiles',
      'uploadSuccessfulCount',
    ]),
  },
  methods: {
    ...mapActions([
      'setUploadedFiles',
      'setAttachmentID',
      'setVideoAttachmentID',
      'setEpisodeAttachmentID',
      'setProgramAttachmentID',
      'inheritAttachment',
      'increaseUploadSuccessfullyCount',
      'decreaseUploadSuccessfullyCount',
      'pushUploadedFile',
      'pushFailedToUpload',
      'pushError',
      'removeAllFailedUploads',
      'loadAttachment',
    ]),
    isSmallerThan100MB(file) {
      if (file.size > (100 * 1024 * 1024)) {
        this.pushError(
          {
            errorKey: 'fileTooLarge',
            errorMessage: `ไฟล์ ${file.name} มีขนาดใหญ่กว่า 100 MB`,
          },
        );
        return false;
      }
      return true;
    },
    onSelectFilesToUpload(e) {
      const files = Array.from(e.target.files);
      this.setActiveModal('FileUploaderModal');
      files.forEach((file) => {
        this.uploadFile(file);
      });
    },
    uploadFile(file) {
      if (!this.isSmallerThan100MB(file)) {
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      let filename = file.name;
      const name = filename.substring(0, filename.lastIndexOf('.'));
      const type = filename.substring(filename.lastIndexOf('.') + 1);
      filename = name;
      const fileInfo = {
        name,
        type,
        filename,
        fileSize: file.size,
        uploadPercentage: 0,
        success: false,
        file,
      };
      this.pushUploadedFile(fileInfo);
      const requestId = file.name;
      const self = this;
      axiosImageAPI.post('/files',
        formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          requestId,
          onUploadProgress(progressEvent) {
            fileInfo.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100), 10) - 20;
          },
          onDownloadProgress() {
            fileInfo.uploadPercentage += 20;
          },
        }).then((response) => {
        self.onUploadSuccessfully(response, fileInfo);
      }).catch((error) => {
        self.onUploadFailed(false, fileInfo, error);
      });
    },
    onUploadSuccessfully(response, fileInfo) {
      this.increaseUploadSuccessfullyCount();
      const uploadResponse = response.data;
      /* eslint-disable no-param-reassign */
      fileInfo.success = true;
      fileInfo.baseUrl = uploadResponse.baseUrl;
    },
    onUploadFailed(isTheFirstTime, fileInfo, error) {
      if (axiosImageAPI.isCancel(error)) {
        return;
      }
      if (!isTheFirstTime) {
        this.pushError(`ไม่สามารถอัปโหลดไฟล์ได้ โปรดติดต่อผู้ดูแลระบบ (Error: ${error.response.status})`);
        return;
      }
      this.pushFailedToUpload(fileInfo);
      if (!error.response) {
        // network error
        this.pushError(`ไฟล์ ${fileInfo.filename} ไม่สามารถอัปโหลดได้ในขณะนี้ โปรดลองอีกครั้ง`);
      } else {
        // http status code
        const code = error.response.status;
        this.pushError(`เกิดความผิดพลาดขณะอัปโหลดไฟล์ ${fileInfo.filename} โปรดติดต่อผู้ดูแลระบบ (Error Code: ${code})`);
      }
    },
    abort(requestId, fileIndex) {
      axiosImageAPI.cancel(requestId);
      this.$delete(this.uploadedFiles, fileIndex);
    },
    abortAll() {
      axiosImageAPI.cancelAll();
      this.removeAllFailedUploads();
    },
    retryUploading(file) {
      const formData = new FormData();
      formData.append('file', file.file);
      const requestId = file.name;
      const self = this;
      /* eslint-disable no-param-reassign */
      axiosImageAPI.post('/files',
        formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          requestId,
          onUploadProgress(progressEvent) {
            file.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100), 10) - 20;
          },
          onDownloadProgress() {
            file.uploadPercentage += 20;
          },
        }).then((response) => {
        self.onUploadSuccessfully(response, file);
      }).catch((error) => {
        self.onUploadFailed(true, file, error);
      });
    },
    retry(filename) {
      if (filename) {
        // Retry a single file
        const fileToRetry = _.filter(this.uploadedFiles, (f) => {
          return f.name === filename;
        });
        this.retryUploading(fileToRetry);
      } else {
        // Retry All
        this.failedToUpload.forEach((file) => {
          this.retryUploading(file);
        });
      }
    },
    onDeleteUploadedFile(fileIndex) {
      this.decreaseUploadSuccessfullyCount();
      this.$delete(this.uploadedFiles, fileIndex);
    },
    allFilesUploaded() {
      if (!this.uploadSuccessfulCount || !this.uploadedFiles.length) {
        return false;
      }
      return this.uploadSuccessfulCount === this.uploadedFiles.length;
    },
    hasError() {
      return !_.isEmpty(this.errors);
    },
    async onUnAttachFile(fileId) {
      await deleteFilesInsideAttachment(this.attachmentID, { fileIds: [fileId] });

      const newFiles = _.filter(this.attachment.files, (file) => {
        return file.id !== fileId;
      });
      const newFileIds = _.map(newFiles, (file) => file.id);

      if (!newFiles.length) {
        switch (this.getCurrentLocation()) {
          case 'videos':
            await this.setVideoAttachmentID(null);
            break;
          case 'programs':
            await this.setProgramAttachmentID(null);
            break;
          case 'episodes':
            await this.setEpisodeAttachmentID(null);
            break;
          default:
            return;
        }
      }

      this.inheritAttachment({ ...this.attachment, files: newFiles, fileIds: newFileIds });
    },
    getCurrentLocation() {
      const [, path] = this.$route.path.split('/');
      return path;
    },
    async updateFilesToAttachment(selectedFiles) {
      let id = this.attachmentID;
      if (id && _.isEmpty(this.attachment)) {
        await this.setAttachmentID(null);
      }
      if (!this.attachmentID) {
        const response = await createAttachment({
          name: 'ไฟล์แนบ',
        });
        id = response.data.id;
        switch (this.getCurrentLocation()) {
          case 'videos':
            await this.setVideoAttachmentID(id);
            break;
          case 'programs':
            await this.setProgramAttachmentID(id);
            break;
          case 'episodes':
            await this.setEpisodeAttachmentID(id);
            break;
          default:
            return;
        }
        await this.setAttachmentID(id);
        await this.loadAttachment(id);
      }

      // add files to attachment
      const filesResponse = await addFilesToAttachment(id, {
        files: selectedFiles,
      });

      let files = [];
      let fileIds = [];
      let attachmentName = 'ไฟล์แนบ';

      if (!_.isEmpty(this.attachment)) {
        attachmentName = this.attachment.name;
        files = _.cloneDeep(this.attachment.files);
        fileIds = [];
        if (this.attachment.fileIds) {
          fileIds = _.cloneDeep(this.attachment.fileIds);
        }
      }

      filesResponse.data.items.forEach((file) => {
        files.push(file);
        fileIds.push(file.id);
      });

      this.inheritAttachment({
        ...this.attachment,
        name: attachmentName,
        files,
        fileIds,
      });
    },
    clearErrors() {
      this.setUploadedFiles(_.filter(this.uploadedFiles, 'success'));
      this.resetErrors();
    },
    getFileIconClass(type) {
      if (!type) {
        return 'fas fa-file-alt';
      }
      const extension = type.toLowerCase();
      let icon = 'fas fa-file';
      switch (extension) {
        case 'pdf':
          icon += '-pdf';
          break;
        case 'doc':
        case 'docx':
          icon += '-word';
          break;
        case 'xls':
        case 'xlsx':
        case 'csv':
          icon += '-excel';
          break;
        case 'ppt':
        case 'pptx':
          icon += '-powerpoint';
          break;
        case 'jpg':
        case 'jpeg':
        case 'jfif':
        case 'png':
        case 'gif':
        case 'svg':
        case 'ico':
        case 'psd':
        case 'ai':
        case 'xd':
        case 'dwg':
          icon += '-image';
          break;
        case 'mp4':
        case 'm4v':
        case 'mov':
        case 'mkv':
        case 'avi':
        case 'wmv':
        case 'm2ts':
        case 'webm':
        case 'flv':
        case 'm3u':
        case 'm3u8':
          icon += '-video';
          break;
        case 'mp3':
        case 'm4a':
        case 'wav':
        case 'flac':
        case 'aif':
        case 'aiff':
        case 'alac':
        case 'ogg':
        case 'opus':
        case 'wma':
          icon += '-audio';
          break;
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
          icon += '-archive';
          break;
        case 'html':
        case 'css':
        case 'scss':
        case 'sass':
        case 'less':
        case 'js':
        case 'jsx':
        case 'asp':
        case 'aspx':
        case 'php':
        case 'vue':
        case 'json':
        case 'java':
        case 'c':
        case 'cs':
        case 'py':
        case 'kt':
        case 'swift':
        case 'xcode':
        case 'mhtml':
          icon += '-code';
          break;
        case 'pages':
        case 'key':
        case 'numbers':
        case 'indd':
        case 'txt':
        default:
          icon += '-alt';
      }
      return icon;
    },
  },
};
